import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AccessMine, ActivityCategory, ActivityEvent, ActivityFeed, ActivityFilters } from '../../types/aai';
import {
  Action, Drawer, EmptyState, ErrorNote, Input, Kicker, Loading, Select, StatusChip,
} from '../../components/aa';
import { relativeTime } from './format';

/*
 * Two reads of one client's history, kept apart the way the guide keeps them:
 *
 *   ActivityView        what happened in the client's work
 *   AccessHistoryView   who changed access, permissions, controls
 *
 * Both are scoped to the active client on the server; nothing here can show a
 * second client. A row opens a drawer rather than a page, so the reader keeps
 * their place in the list. The drawer states the record and links to it; it
 * never shows a backend payload.
 */

const CATEGORY_LABEL: Record<string, string> = {
  all: 'All activity',
  projects: 'Projects',
  orbit: 'Orbit',
  quick_wins: 'Quick Wins',
  adoption: 'AI Adoption',
  context: 'Context',
  results: 'Results',
  access: 'Access & controls',
  billing: 'Billing',
  workspace: 'Workspace',
};

const FILTERS: (ActivityCategory | 'all')[] = [
  'all', 'projects', 'orbit', 'quick_wins', 'adoption', 'context', 'results', 'access', 'billing',
];

const ENTITY_LABEL: Record<string, string> = {
  request: 'Project',
  orbit_collection: 'Orbit collection',
  skill: 'Quick Win',
  member: 'Workspace member',
  source: 'Source',
  client: 'Client',
  portfolio_recommendation: 'Portfolio recommendation',
};

export type ActivityLink = { label: string; to: string } | null;
export type ActivityLinkResolver = (event: ActivityEvent) => ActivityLink;

// Where the related record lives in the partner console. An entity with no
// destination gets no link rather than a guessed one. The Owner's Company
// Context passes its own resolver, since its records live on the Owner's pages.
const linkFor: ActivityLinkResolver = (event) => {
  if (!event.entity_type) return null;
  if (event.entity_type === 'request' && event.entity_id) {
    return { label: 'Open project', to: `/partner/projects/${event.entity_id}` };
  }
  if (event.entity_type === 'orbit_collection' && event.entity_id) {
    return { label: 'Open the Room', to: '/partner/context' };
  }
  if (event.entity_type === 'skill') return { label: 'Open client', to: '/partner/client' };
  if (event.entity_type === 'member') return { label: 'Open people', to: '/partner/settings?section=people' };
  if (event.entity_type === 'source') return { label: 'Open company tools', to: '/partner/context?tab=sources' };
  if (event.entity_type === 'client') return { label: 'Open client', to: '/partner/client' };
  return null;
};

const ROLE_WORD: Record<string, string> = {
  consultant: 'Solution Partner',
  super: 'Platform team',
  admin: 'Platform team',
  owner: 'Client owner',
  client: 'Client owner',
  member: 'Employee',
};

const roleWord = (raw: string | null): string => {
  if (!raw) return '';
  const key = raw.toLowerCase();
  if (ROLE_WORD[key]) return ROLE_WORD[key];
  const words = key.replace(/_/g, ' ').replace(/^ai\b/, 'AI');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const actorLine = (event: ActivityEvent): string => {
  const who = event.actor_email || 'The company';
  const role = roleWord(event.actor_role);
  if (!role) return who;
  return `${who} · ${role}`;
};

const scopeOf = (event: ActivityEvent): string => {
  if (event.entity_title) return event.entity_title;
  if (event.entity_type && ENTITY_LABEL[event.entity_type]) return ENTITY_LABEL[event.entity_type];
  return CATEGORY_LABEL[event.category] || 'Workspace';
};

const whenExact = (iso: string | null): string => {
  if (!iso) return 'Time not recorded';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const changeWord = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'None';
  if (value === true) return 'On';
  if (value === false) return 'Off';
  return String(value).replace(/_/g, ' ');
};

const changeLine = (event: ActivityEvent): string | null => {
  if (!event.change) return null;
  return `${changeWord(event.change.from)} to ${changeWord(event.change.to)}`;
};

const TRUST_LABEL: Record<string, string> = { trusted: 'Trusted', record_only: 'Record only' };

// What the reader sees when the server narrows the scope they asked for.
const RESTRICTED_NOTE: Record<string, string> = {
  company: 'Your assignment does not include this client’s full history. Only your own events are shown.',
  department: 'Activity here is limited to your department and the actions sent to you.',
  mine: 'Only events you took, events taken on you, and company events with no named person appear here.',
};

/* ── shared pieces ─────────────────────────────────────────────────────── */

const FactCard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex min-w-[200px] flex-1 basis-[calc(50%-6px)] flex-col gap-2 rounded-[13px] border border-line bg-canvas px-4 py-4">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-dim">{label}</span>
    <strong className="text-[13.5px] font-bold leading-[1.35] text-ink">{value}</strong>
  </div>
);

export const EventDrawer: React.FC<{
  event: ActivityEvent;
  clientName: string;
  kicker: string;
  layer: 'page' | 'modal';
  links?: ActivityLinkResolver;
  onClose: () => void;
}> = ({ event, clientName, kicker, layer, links = linkFor, onClose }) => {
  const navigate = useNavigate();
  const link = links(event);
  const change = changeLine(event);
  return (
    <Drawer layer={layer} onClose={onClose} kicker={<Kicker>{kicker}</Kicker>} title={event.label}>
      <div className="flex flex-wrap gap-3">
        <FactCard label="Client" value={clientName} />
        <FactCard label={event.entity_type ? ENTITY_LABEL[event.entity_type] || 'Related record' : 'Related record'} value={scopeOf(event)} />
        <FactCard label="Actor" value={actorLine(event)} />
        <FactCard label="Time" value={whenExact(event.created_at)} />
        <FactCard label="Type" value={CATEGORY_LABEL[event.category] || 'Workspace'} />
        {event.target_email && <FactCard label="Concerning" value={event.target_email} />}
        {change && <FactCard label="Change" value={change} />}
        {typeof event.detail?.sent_to === 'string' && <FactCard label="Sent to" value={String(event.detail.sent_to)} />}
        {typeof event.detail?.response_note === 'string' && <FactCard label="Note" value={String(event.detail.response_note)} />}
        <FactCard label="Trust state" value={TRUST_LABEL[event.trust] || event.trust} />
      </div>
      <div className="-mx-5 mt-6 flex items-center justify-between gap-3 border-t border-line px-5 pt-4 sm:-mx-[30px] sm:px-[30px]">
        <span>{link && <Action arrow onClick={() => { onClose(); navigate(link.to); }}>{link.label}</Action>}</span>
        <Action variant="quiet" onClick={onClose}>Close</Action>
      </div>
    </Drawer>
  );
};

const ROW = 'flex min-h-[74px] w-full items-center gap-4 border-t border-line px-5 py-3.5 text-left transition-colors first:border-t-0 hover:bg-[#fbfbfa]';

const Column: React.FC<{ label: string; value: React.ReactNode; grow: string }> = ({ label, value, grow }) => (
  <span className={clsx('hidden min-w-0 flex-col gap-1 md:flex', grow)}>
    <small className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</small>
    <strong className="truncate text-[13px] font-semibold leading-[1.35] text-ink">{value}</strong>
  </span>
);

/* ── Activity ──────────────────────────────────────────────────────────── */

const ActivityRow: React.FC<{ event: ActivityEvent; onOpen: () => void }> = ({ event, onOpen }) => (
  <button type="button" onClick={onOpen} className={ROW}>
    <span className="flex min-w-0 flex-[1.5] flex-col gap-1">
      <span className="flex items-center gap-2">
        <strong className="truncate text-[13.5px] font-bold text-ink">{event.label}</strong>
        <StatusChip status={event.trust} label={TRUST_LABEL[event.trust]} />
      </span>
      <small className="truncate text-[12.5px] text-ink-muted">{actorLine(event)}</small>
    </span>
    <Column label="Scope" value={scopeOf(event)} grow="flex-[0.9]" />
    <Column label="When" value={relativeTime(event.created_at)} grow="flex-[0.6]" />
    <ArrowRight size={16} className="shrink-0 text-ink-soft" />
  </button>
);

const FilterPills: React.FC<{ active: string; onChange: (c: ActivityCategory | 'all') => void }> = ({ active, onChange }) => (
  <div role="group" aria-label="Activity filters" className="flex flex-wrap gap-1.5">
    {FILTERS.map((f) => (
      <button
        key={f}
        type="button"
        aria-pressed={active === f}
        onClick={() => onChange(f)}
        className={clsx(
          'h-11 rounded-lg px-4 text-[13.5px] font-semibold transition-colors',
          active === f && 'border border-line bg-canvas-inset text-ink',
          active !== f && 'text-ink-muted hover:bg-canvas-inset hover:text-ink',
        )}
      >
        {CATEGORY_LABEL[f]}
      </button>
    ))}
  </div>
);

const endOfDay = (date: string): string | undefined => {
  if (!date) return undefined;
  return `${date}T23:59:59`;
};

export const ActivityView: React.FC<{
  clientId: string; clientName: string; layer?: 'page' | 'modal'; links?: ActivityLinkResolver;
  /** The scope the viewer holds. A department head asks for their department;
   *  the server still decides and the view says so when it narrows. */
  scope?: 'company' | 'department' | 'mine';
}> = ({
  clientId, clientName, layer = 'page', links, scope = 'company',
}) => {
  const [category, setCategory] = useState<ActivityCategory | 'all'>('all');
  const [q, setQ] = useState('');
  const [actor, setActor] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [selected, setSelected] = useState<ActivityEvent | null>(null);

  const filters: ActivityFilters = { category, q: q.trim(), actor, since: since || undefined, until: endOfDay(until) };
  const feed = useAsync<ActivityFeed>(
    () => aaiApi.getActivity(scope, 100, filters),
    'Could not load this client’s activity.',
    [clientId, category, q, actor, since, until, scope],
  );
  const loadActors = (): Promise<{ data: { actors: string[] } }> => {
    if (scope !== 'company') return Promise.resolve({ data: { actors: [] } });
    return aaiApi.getActivityActors();
  };
  const actors = useAsync<{ actors: string[] }>(loadActors, 'Could not load who acted here.', [clientId, scope]);

  const events = feed.data?.events || [];
  const restricted = Boolean(feed.data && feed.data.scope !== scope);

  return (
    <div className="flex flex-col gap-3.5">
      <FilterPills active={category} onChange={setCategory} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[220px] flex-1">
          <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activity" aria-label="Search activity" />
        </div>
        <div className="w-[160px]"><Input type="date" value={since} onChange={(e) => setSince(e.target.value)} aria-label="From date" /></div>
        <div className="w-[160px]"><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="To date" /></div>
        <div className="w-[240px]">
          <Select value={actor} onChange={(e) => setActor(e.target.value)} aria-label="Actor">
            <option value="">Anyone</option>
            {(actors.data?.actors || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-panel border border-line bg-white">
        {feed.loading && <Loading label="Loading activity" />}
        {feed.error && <div className="px-6 py-5"><ErrorNote message={feed.error} /></div>}
        {!feed.loading && !feed.error && restricted && (
          <div className="px-6 py-5"><ErrorNote message={RESTRICTED_NOTE[scope] || RESTRICTED_NOTE.company} /></div>
        )}
        {!feed.loading && !feed.error && events.length === 0 && (
          <EmptyState title="Nothing recorded here" reason="Events appear as work happens in this client: requests, decisions, Orbit collections, published Quick Wins, and program changes." />
        )}
        {events.map((e) => <ActivityRow key={e.id} event={e} onOpen={() => setSelected(e)} />)}
      </div>

      {selected && (
        <EventDrawer event={selected} clientName={clientName} kicker="Activity" layer={layer} links={links} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

/* ── Access history ────────────────────────────────────────────────────── */

const notRecorded = (value: string | null | undefined): string => value || 'Not recorded';

const countWord = (n: number, one: string, many: string): string => {
  if (n === 1) return `1 ${one}`;
  return `${n} ${many}`;
};

const YourAccess: React.FC<{ access: AccessMine }> = ({ access }) => (
  <div className="flex flex-wrap gap-3">
    <FactCard label="Role" value={access.role} />
    <FactCard label="Assignment" value={notRecorded(access.assignment)} />
    <FactCard label="Valid until" value={notRecorded(access.valid_until)} />
    <FactCard label="Project access" value={countWord(access.project_access.count, 'project', 'projects')} />
    <FactCard label="Workspace access" value={notRecorded(access.workspace_access.name)} />
    <FactCard label="Source access" value={countWord(access.source_access.count, 'approved source', 'approved sources')} />
  </div>
);

const AccessRow: React.FC<{ event: ActivityEvent; onOpen: () => void }> = ({ event, onOpen }) => {
  const change = changeLine(event);
  return (
    <button type="button" onClick={onOpen} className={ROW}>
      <span className="flex min-w-0 flex-[1.5] flex-col gap-1">
        <strong className="truncate text-[13.5px] font-bold text-ink">{event.label}</strong>
        <small className="truncate text-[12.5px] text-ink-muted">
          {event.target_email || scopeOf(event)}
          {change && ` · ${change}`}
        </small>
      </span>
      <Column label="Changed by" value={actorLine(event)} grow="flex-[0.9]" />
      <Column label="Date" value={whenExact(event.created_at)} grow="flex-[0.7]" />
      <ArrowRight size={16} className="shrink-0 text-ink-soft" />
    </button>
  );
};

export const AccessHistoryView: React.FC<{ clientId: string; clientName: string; layer?: 'page' | 'modal' }> = ({
  clientId, clientName, layer = 'page',
}) => {
  const [selected, setSelected] = useState<ActivityEvent | null>(null);
  const mine = useAsync<AccessMine>(() => aaiApi.getMyAccess(), 'Could not load your access.', [clientId]);
  const history = useAsync<{ events: ActivityEvent[] }>(
    () => aaiApi.getAccessHistory(100), 'Could not load this client’s access history.', [clientId],
  );
  const events = history.data?.events || [];

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h3 className="m-0 mb-3 text-[16px] font-bold text-ink">Your access</h3>
        {mine.loading && <Loading label="Loading your access" />}
        {mine.error && <ErrorNote message={mine.error} />}
        {mine.data && <YourAccess access={mine.data} />}
      </section>

      <section>
        <h3 className="m-0 mb-1 text-[16px] font-bold text-ink">Access history</h3>
        <p className="m-0 mb-3 text-[13px] leading-[21px] text-ink-muted">
          Who received access, who changed it, what changed, and when. Recording started when this history shipped; earlier changes are not listed.
        </p>
        <div className="overflow-hidden rounded-panel border border-line bg-white">
          {history.loading && <Loading label="Loading access history" />}
          {history.error && <div className="px-6 py-5"><ErrorNote message={history.error} /></div>}
          {!history.loading && !history.error && events.length === 0 && (
            <EmptyState title="No access changes recorded" reason="Membership grants, role changes, source permissions, and policy changes appear here as they happen." />
          )}
          {events.map((e) => <AccessRow key={e.id} event={e} onOpen={() => setSelected(e)} />)}
        </div>
      </section>

      {selected && (
        <EventDrawer event={selected} clientName={clientName} kicker="Access history" layer={layer} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};
